import { wordCount } from '@concord/domain';
import type { IntakeView } from '@concord/domain/contracts';

export interface StepDef {
  n: number;
  key: string;
  label: string;
  /** "Next: …" on the step line. */
  short: string;
  /** The step line's title for the current step. */
  title: string;
}

export const STEPS: StepDef[] = [
  { n: 1, key: 'type', label: 'Case type', short: 'case type', title: 'Case type' },
  { n: 2, key: 'panel', label: 'Expert panel', short: 'expert panel', title: 'Expert panel' },
  { n: 3, key: 'about', label: 'About you', short: 'about you', title: 'About you' },
  {
    n: 4,
    key: 'history',
    label: 'History and questions',
    short: 'your history',
    title: 'Your history and questions',
  },
  { n: 5, key: 'records', label: 'Records', short: 'your records', title: 'Your records' },
  { n: 6, key: 'times', label: "Times you're free", short: "times you're free", title: "Times you're free" },
  { n: 7, key: 'review', label: 'Review and send', short: 'review and send', title: 'Review and send' },
];

export const stepByKey = (key: string | undefined) => STEPS.find((s) => s.key === key) ?? STEPS[0]!;

export function isStepDone(v: IntakeView, key: string): boolean {
  switch (key) {
    case 'type':
      return v.caseType !== null;
    case 'panel':
      return v.pool !== null;
    case 'about':
      return v.demographicsVolunteered !== null;
    case 'history':
      return v.history.trim().length > 0 && v.questions.length > 0;
    case 'records':
      return v.attachments.every((a) => a.uploaded && a.description) && v.intakeStep > 5;
    case 'times':
      return v.times.length > 0;
    default:
      return false;
  }
}

/** The right-hand summary's one-line value for each step. */
export function summaryValue(v: IntakeView, key: string): { value: string; sub?: string; soft: boolean } {
  switch (key) {
    case 'type':
      return v.caseType ? { value: v.caseType.term, soft: false } : { value: 'Not chosen yet', soft: true };
    case 'panel':
      return v.pool ? { value: v.pool.publicName, soft: false } : { value: 'Not chosen yet', soft: true };
    case 'about':
      if (v.demographicsVolunteered === false) return { value: 'Not provided · optional', soft: false };
      if (v.demographicsVolunteered && v.age !== null) {
        return {
          value: `${v.age}${v.gender ? `, ${v.gender}` : ''}`,
          sub: 'optional, provided',
          soft: false,
        };
      }
      return { value: 'Optional', soft: true };
    case 'history': {
      const w = wordCount(v.history);
      if (!w && !v.questions.length) return { value: 'Not started', soft: true };
      return {
        value: `${w} ${w === 1 ? 'word' : 'words'} · ${v.questions.length} ${v.questions.length === 1 ? 'question' : 'questions'}`,
        soft: false,
      };
    }
    case 'records': {
      if (!v.attachments.length)
        return { value: v.intakeStep > 5 ? 'None added' : 'Next', soft: v.intakeStep <= 5 };
      const uploading = v.attachments.filter((a) => !a.uploaded).length;
      const needs = v.attachments.filter((a) => a.uploaded && !a.description).length;
      const ready = v.attachments.length - uploading - needs;
      if (!uploading && !needs) {
        return { value: `${ready} ${ready === 1 ? 'file' : 'files'}, all described`, soft: false };
      }
      return {
        value: [
          ready && `${ready} ready`,
          uploading && `${uploading} uploading`,
          needs && `${needs} need a description`,
        ]
          .filter(Boolean)
          .join(' · '),
        soft: false,
      };
    }
    case 'times': {
      if (!v.times.length)
        return { value: v.intakeStep >= 6 ? 'None offered yet' : 'After records', soft: true };
      const days = new Set(v.times.map((t) => t.date)).size;
      return {
        value: `${v.times.length} ${v.times.length === 1 ? 'time' : 'times'} over ${days} ${days === 1 ? 'day' : 'days'}`,
        soft: false,
      };
    }
    default:
      return { value: "You'll see everything before it goes", soft: true };
  }
}
